import { useEffect, useState } from 'react';
import type { TourStop } from 'fym-shared/src/music24/onboardingTour';
import type { TourBox } from './tourCardPlacement';

/**
 * The page part of a stop. Each stop page marks it with `data-tour="<stop>"` (Assets grid, Plan,
 * SmartLink cards, Streaming chart, Home rows). A part with no size (a hidden tab) does not count.
 */
export function findTourTarget(stop: TourStop): HTMLElement | null {
  const element = document.querySelector<HTMLElement>(`[data-tour="${stop}"]`);
  if (element === null) {
    return null;
  }
  const box = element.getBoundingClientRect();
  return box.width > 0 || box.height > 0 ? element : null;
}

function sameBox(a: TourBox | null, b: TourBox): boolean {
  return a !== null && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;
}

/** The first ancestor that scrolls, else the page. The backdrop sends the wheel to it. */
export function scrollParentOf(element: HTMLElement | null): Element | null {
  let node = element?.parentElement ?? null;
  while (node !== null && node !== document.body) {
    const { overflowY } = window.getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return document.scrollingElement;
}

export interface TourTarget {
  element: HTMLElement | null;
  /** The viewport box of the element; null while the page has no target. */
  box: TourBox | null;
}

/**
 * Finds the target of the stop and follows its box. The stop page loads after the route change,
 * so a MutationObserver looks again after each DOM change (once per frame) until the part shows,
 * and finds a part that the page drew again. A ResizeObserver, the scroll of any scroller and the
 * window resize move the box.
 */
export function useTourTarget(stop: TourStop | null): TourTarget {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [box, setBox] = useState<TourBox | null>(null);

  useEffect(() => {
    if (stop === null) {
      setElement(null);
      return undefined;
    }
    let frame = 0;
    const find = (): void => {
      frame = 0;
      const found = findTourTarget(stop);
      setElement((current) => (current === found ? current : found));
    };
    find();
    const observer = new MutationObserver(() => {
      if (frame === 0) {
        frame = window.requestAnimationFrame(find);
      }
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-tour'],
    });
    return () => {
      observer.disconnect();
      if (frame !== 0) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [stop]);

  useEffect(() => {
    if (element === null) {
      setBox(null);
      return undefined;
    }
    let frame = 0;
    const measure = (): void => {
      frame = 0;
      const rect = element.getBoundingClientRect();
      const next: TourBox = { top: rect.top, left: rect.left, width: rect.width, height: rect.height };
      setBox((current) => (sameBox(current, next) ? current : next));
    };
    const schedule = (): void => {
      if (frame === 0) {
        frame = window.requestAnimationFrame(measure);
      }
    };
    measure();
    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    resizeObserver?.observe(element);
    // Capture: the app scrolls in inner containers too, and a scroll event does not bubble.
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      if (frame !== 0) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [element]);

  return { element, box };
}
